import {
    CalendarIcon,
    CheckCircleIcon,
    CurrencyRupeeIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    PlusIcon,
    StarIcon,
    TrashIcon,
    UserIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { getAgentName } from '../../utils/agentUtils';
import AdminApiService from '../../utils/api';
import PhoneActions from '../../components/PhoneActions';

const StatusBadge = ({ status }) => {
  const statusType = status || 'inactive';
  const styles = {
    active: 'bg-success-100 text-success-800 border-success-200',
    inactive: 'bg-gray-100 text-gray-800 border-gray-200',
    suspended: 'bg-danger-100 text-danger-800 border-danger-200'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${styles[statusType]}`}>
      {statusType.charAt(0).toUpperCase() + statusType.slice(1)}
    </span>
  );
};

const MembershipBadge = ({ membership }) => {
  // Handle if membership is an object, null, undefined, or string
  let membershipType = 'basic';
  
  if (typeof membership === 'string') {
    membershipType = membership.toLowerCase();
  } else if (membership && typeof membership === 'object' && membership.type) {
    membershipType = membership.type.toLowerCase();
  }
  
  const styles = {
    premium: 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white',
    basic: 'bg-gray-100 text-gray-800'
  };

  const displayText = membershipType.charAt(0).toUpperCase() + membershipType.slice(1);

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[membershipType] || styles.basic}`}>
      {membershipType === 'premium' && <StarIcon className="w-3 h-3 mr-1" />}
      {displayText}
    </span>
  );
};

const getAgentInitial = (agent) => {
  const name = getAgentName(agent);
  const initial = name?.trim()?.charAt(0)?.toUpperCase();
  return initial || 'A';
};


// Where a seller or agent is based. Accounts keep it in `address`; a few
// older records used `location`.
const placeOf = (u) => {
  const a = (u && u.address) || {};
  const l = (u && typeof u.location === 'object' && u.location) || {};
  return {
    city: String(a.city || l.city || '').trim(),
    state: String(a.state || l.state || '').trim(),
  };
};

const NOT_SET = '__not_set__';

/** "Andhra Pradesh 12 · Telangana 4 · Not set 3" - click one to filter. */
const StateSummary = ({ people, value, onChange, noun }) => {
  const counts = {};
  let unset = 0;
  people.forEach((p) => {
    const { state } = placeOf(p);
    if (state) counts[state] = (counts[state] || 0) + 1;
    else unset += 1;
  });
  const states = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  if (!states.length && !unset) return null;
  const pill = (key, label, n, warn) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(value === key ? 'all' : key)}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
        value === key
          ? 'bg-primary-600 text-white border-primary-600'
          : warn
            ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
      }`}
    >
      {label} <span className="font-semibold">{n}</span>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-500 mr-1">{noun} by state:</span>
      {states.map((s) => pill(s, s, counts[s]))}
      {unset ? pill(NOT_SET, 'Location not set', unset, true) : null}
    </div>
  );
};

const AgentCard = ({ agent, onAction }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4, scale: 1.02 }}
    className="bg-white rounded-xl p-6 shadow-soft border border-gray-400 hover:shadow-lg transition-all duration-300"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center space-x-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center text-2xl font-bold">
            {getAgentInitial(agent)}
          </div>
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
            agent.isActive ? 'bg-success-500' : 'bg-gray-400'
          }`}></div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{getAgentName(agent)}</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
            <MapPinIcon className="w-4 h-4" />
            {placeOf(agent).state ? (
              <span>{[placeOf(agent).city, placeOf(agent).state].filter(Boolean).join(', ')}</span>
            ) : (
              <span className="text-amber-700">Location not set</span>
            )}
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
            <CalendarIcon className="w-4 h-4" />
            <span>Joined {new Date(agent.createdAt || agent.joinDate).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end space-y-2">
        <StatusBadge status={agent.isActive ? 'active' : 'inactive'} />
        <MembershipBadge membership={agent.membership || 'basic'} />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="text-center p-3 bg-primary-50 rounded-lg">
        <div className="text-2xl font-bold text-primary-600">{agent.stats?.totalCarsListed || 0}</div>
        <div className="text-xs text-gray-600">Cars Listed</div>
      </div>
      <div className="text-center p-3 bg-success-50 rounded-lg">
        <div className="text-2xl font-bold text-success-600">{agent.stats?.carsSold || 0}</div>
        <div className="text-xs text-gray-600">Cars Sold</div>
      </div>
      <div className="text-center p-3 bg-secondary-50 rounded-lg">
          <div className="text-2xl font-bold text-secondary-600">{
            typeof agent.rating === 'object' && agent.rating !== null
              ? agent.rating.average ?? 0
              : agent.rating || 0
          }</div>
        <div className="text-xs text-gray-600">Rating</div>
      </div>
      <div className="text-center p-3 bg-warning-50 rounded-lg">
        <div className="text-2xl font-bold text-warning-600">₹{((agent.stats?.totalEarnings || 0) / 100000).toFixed(1)}L</div>
        <div className="text-xs text-gray-600">Total Earnings</div>
      </div>
    </div>

    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
      <div className="flex items-center space-x-2">
        <PhoneActions phone={agent.phoneNumber || agent.phone} textClassName="text-gray-600 font-normal" />
      </div>
      <div className="flex items-center space-x-2">
        <Link
          to={`/agents/${agent._id || agent.id}`}
          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          <EyeIcon className="w-4 h-4" />
        </Link>
        <button
          onClick={() => onAction(agent.isActive ? 'deactivate' : 'activate', agent)}
          className={`p-2 rounded-lg transition-colors ${
            agent.isActive
              ? 'text-gray-400 hover:text-danger-600 hover:bg-danger-50'
              : 'text-gray-400 hover:text-success-600 hover:bg-success-50'
          }`}
        >
          {agent.isActive ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onAction('delete', agent)}
          className="p-2 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  </motion.div>
);

const AgentManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [membershipFilter, setMembershipFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await AdminApiService.getAgents();
      const agentsData = response.data || response;
      setAgents(Array.isArray(agentsData) ? agentsData : []);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      toast.error('Failed to load agents data');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedAgents = useMemo(() => {
    return agents
      .filter(agent => {
        const agentName = getAgentName(agent);
        const place = placeOf(agent);
        const locationStr = `${place.city} ${place.state}`.toLowerCase();
        
        const matchesSearch = agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (agent.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                            (agent.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                            locationStr.includes(searchTerm.toLowerCase());
        
        const agentMembership = typeof agent.membership === 'object' && agent.membership?.type 
          ? agent.membership.type 
          : agent.membership || 'basic';
        
        const matchesStatus = statusFilter === 'all' || (agent.isActive ? 'active' : 'inactive') === statusFilter;
        const matchesMembership = membershipFilter === 'all' || agentMembership === membershipFilter;
        const matchesState = stateFilter === 'all'
          || (stateFilter === NOT_SET ? !place.state : place.state === stateFilter);
        return matchesSearch && matchesStatus && matchesMembership && matchesState;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return getAgentName(a).localeCompare(getAgentName(b));
          case 'earnings':
            return (b.totalEarnings || 0) - (a.totalEarnings || 0);
          case 'rating':
            return (b.rating || 0) - (a.rating || 0);
          case 'joinDate':
            return new Date(b.createdAt || b.joinDate) - new Date(a.createdAt || a.joinDate);
          default:
            return 0;
        }
      });
  }, [searchTerm, statusFilter, membershipFilter, stateFilter, sortBy, agents]);

  const handleAction = async (action, agent) => {
    const agentName = getAgentName(agent);
    const agentId = agent._id || agent.id;

    try {
      switch (action) {
        case 'activate':
          await AdminApiService.unblockAgent(agentId);
          toast.success(`${agentName} has been activated`);
          await fetchAgents(); // Refresh the list
          break;

        case 'deactivate':
          await AdminApiService.blockAgent(agentId);
          toast.error(`${agentName} has been deactivated`);
          await fetchAgents(); // Refresh the list
          break;

        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${agentName}?`)) {
            await AdminApiService.deleteAgent(agentId);
            toast.success(`${agentName} has been deleted`);
            await fetchAgents(); // Refresh the list
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.error(`Failed to ${action} agent:`, error);
      toast.error(`Failed to ${action} ${agentName}. Please try again.`);
    }
  };

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.isActive).length,
    premium: agents.filter(a => {
      const membership = typeof a.membership === 'object' && a.membership?.type 
        ? a.membership.type 
        : a.membership;
      return membership === 'premium';
    }).length,
    totalEarnings: agents.reduce((sum, a) => sum + (a.stats?.totalEarnings || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h1 className="text-3xl font-display font-bold text-gray-900">
            Agent Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage agents, memberships, and monitor performance
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center space-x-4 mt-4 lg:mt-0"
        >
          <Link
            to="/agents/create"
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Agent</span>
          </Link>
        </motion.div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="p-4 bg-primary-100 rounded-xl">
              <UserIcon className="w-8 h-8 text-primary-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Agents</p>
              <p className="text-3xl font-bold text-success-600 mt-2">{stats.active}</p>
            </div>
            <div className="p-4 bg-success-100 rounded-xl">
              <CheckCircleIcon className="w-8 h-8 text-success-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Premium Members</p>
              <p className="text-3xl font-bold text-warning-600 mt-2">{stats.premium}</p>
            </div>
            <div className="p-4 bg-warning-100 rounded-xl">
              <StarIcon className="w-8 h-8 text-warning-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-3xl font-bold text-secondary-600 mt-2">₹{(stats.totalEarnings / 10000000).toFixed(1)}Cr</p>
            </div>
            <div className="p-4 bg-secondary-100 rounded-xl">
              <CurrencyRupeeIcon className="w-8 h-8 text-secondary-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-64"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            
            <select
              value={membershipFilter}
              onChange={(e) => setMembershipFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Memberships</option>
              <option value="premium">Premium</option>
              <option value="basic">Basic</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="name">Sort by Name</option>
              <option value="earnings">Sort by Earnings</option>
              <option value="rating">Sort by Rating</option>
              <option value="joinDate">Sort by Join Date</option>
            </select>
            <span className="text-sm text-gray-500">
              {filteredAndSortedAgents.length} of {agents.length} agents
            </span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <StateSummary people={agents} value={stateFilter} onChange={setStateFilter} noun="Agents" />
        </div>
      </motion.div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredAndSortedAgents.map((agent, index) => (
          <motion.div
            key={agent.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <AgentCard agent={agent} onAction={handleAction} />
          </motion.div>
        ))}
      </div>

      {filteredAndSortedAgents.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <UserIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No agents found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </motion.div>
      )}
    </div>
  );
};

export default AgentManagement;